import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '../Card/Card';
import { Badge } from '../Badge/Badge';
import { cn } from '@/lib/utils';
import { __resolve } from '@/lib/i18n';

export interface QuickActionItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  count?: number;
  comingSoon?: boolean;
  onClick?: () => void;
}

interface QuickActionsProps {
  title?: string;
  items: QuickActionItem[];
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}

export const QuickActions = React.forwardRef<HTMLDivElement, QuickActionsProps>(
  ({ title = __resolve('ui::Quick Actions'), items, columns = 2, className }, ref) => {
    const gridCols = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
    };

    return (
      <div ref={ref} className={cn(className)}>
        {title && (
          <h2 className="text-lg font-semibold mb-4">{title}</h2>
        )}
        <div className={cn('grid gap-4', gridCols[columns])}>
          {items.map((item, index) => (
            <Card
              key={index} 
              interactive={!item.comingSoon}
              className={cn(
                'h-full transition-all duration-200',
                item.comingSoon && 'opacity-60'
              )}
              onClick={() => {
                if (!item.comingSoon) {
                  if (item.onClick) {
                    item.onClick();
                  }
                }
              }}
            >
              <CardHeader className="space-y-3">
                {/* Mobile-first layout: stacked on mobile, side-by-side on desktop */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  {/* Icon, title, description section */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Icon - properly centered */}
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                      item.comingSoon 
                        ? 'bg-muted text-muted-foreground' 
                        : 'bg-primary-100 text-primary-600'
                    )}>
                      {item.icon}
                    </div>
                    
                    {/* Title and description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-base leading-tight">
                          {item.title}
                        </CardTitle>
                        {/* Count badge - inline with title on all screen sizes */}
                        {item.count !== undefined && (
                          <Badge variant="secondary" className="text-xs">
                            {item.count}
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="text-sm mt-1 leading-relaxed">
                        {item.description}
                      </CardDescription>
                      {/* Coming Soon badge - below title/description on mobile */}
                      {item.comingSoon && (
                        <div className="mt-2 sm:hidden">
                          <Badge variant="outline" className="text-xs">
                            {__resolve('ui::Coming Soon')}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Desktop: badges on the right */}
                  {item.comingSoon && (
                    <div className="hidden sm:flex items-start shrink-0">
                      <Badge variant="outline" className="text-xs">
                        Coming Soon
                      </Badge>
                    </div>
                  )}
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }
);

QuickActions.displayName = 'QuickActions';